import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Collapse,
  IconButton,
  Grid,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';

interface HowItWorksStep {
  title: string;
  description: string;
  image: string;
}

interface HowItWorksCardProps {
  title: string;
  steps: HowItWorksStep[];
  defaultExpanded?: boolean;
}

const ExpandMoreIconStyled = styled(IconButton)(({ theme }) => ({
  transform: 'rotate(0deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
  '&.expanded': {
    transform: 'rotate(180deg)',
  },
}));

const StepImage = styled('img')({
  width: '100%',
  maxWidth: 200,
  height: 'auto',
  objectFit: 'contain',
});

const StepNumber = styled(Box)(({ theme }) => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: '0.875rem',
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.primary.main,
  margin: '0 auto 8px',
}));

export default function HowItWorksCard({
  title,
  steps,
  defaultExpanded = true,
}: HowItWorksCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Card
      sx={{
        mb: 3,
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <ExpandMoreIconStyled
            className={expanded ? 'expanded' : ''}
            size="small"
          >
            <ExpandMoreIcon />
          </ExpandMoreIconStyled>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              {steps.map((step, index) => (
                <Grid item xs={12} sm={6} md={12 / steps.length} key={index}>
                  <Box sx={{ textAlign: 'center' }}>
                    <StepNumber>{index + 1}</StepNumber>
                    <StepImage src={step.image} alt={step.title} />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        mt: 2,
                        mb: 1,
                        color: '#1e293b',
                      }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        lineHeight: 1.5,
                      }}
                    >
                      {step.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}
